import { SAFE_COORDINATE_OUTSIDE_MAP } from "@/config/constants";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import { type TileIndex, type TileNeighbour } from "@/types/tile";
import { formatNumber } from "@/utils/format";
import IndustrialLumberMillBuildingTileEntity from "../buildings/IndustrialLumberMillBuildingTileEntity";
import LumberMillBuildingTileEntity from "../buildings/LumberMillBuildingTileEntity";
import WaterSawMillBuildingTileEntity from "../buildings/WaterSawMillBuildingTileEntity";
import { FrameName, tilerepoFriendlyUnitsFrameNames } from "../config";
import { ResourceType } from "../config/resources";
import TileEntity from "../TileEntity";

type ForestFrameNames =
	| FrameName.FOREST
	| FrameName.DENSE_FOREST
	| FrameName.OLD_FOREST;

class ForestTerrainTileEntity extends TileEntity {
	public static readonly frameName: ForestFrameNames = FrameName.FOREST;

	public get description(): string {
		return super.description.replace(
			"{number}",
			formatNumber(
				this.config.production.get(
					ResourceType.PRODUCTION_RESOURCE_UNIT,
				)! as number,
			),
		);
	}

	// TODO: toto treba refaktornut cez suppliesTileIndexes, ale aby to zase nebol overkill na vypocet, kedze kazdy len to pouziva kazdy tick
	public get assignedLumberMillTileIndexes(): Array<TileIndex> {
		const { range: lumberMillRange } = new LumberMillBuildingTileEntity(
			{ x: SAFE_COORDINATE_OUTSIDE_MAP, y: SAFE_COORDINATE_OUTSIDE_MAP },
			this.tilerepo,
		);
		const { range: industrialLumberMillRange } =
			new IndustrialLumberMillBuildingTileEntity(
				{
					x: SAFE_COORDINATE_OUTSIDE_MAP,
					y: SAFE_COORDINATE_OUTSIDE_MAP,
				},
				this.tilerepo,
			);
		const { range: waterSawMillRange } = new WaterSawMillBuildingTileEntity(
			{ x: SAFE_COORDINATE_OUTSIDE_MAP, y: SAFE_COORDINATE_OUTSIDE_MAP },
			this.tilerepo,
		);

		if (
			!lumberMillRange ||
			!industrialLumberMillRange ||
			!waterSawMillRange
		)
			throw new Error("Range is not defined!");

		const lumberMills = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				lumberMillRange,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					neighbour.frameName === FrameName.LUMBER_MILL,
			) as Array<TileNeighbour>;

		const industrialLumberMills = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				industrialLumberMillRange,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					neighbour.frameName === FrameName.INDUSTRIAL_LUMBER_MILL,
			) as Array<TileNeighbour>;

		const waterSawMills = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				waterSawMillRange,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [, , tileId] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					neighbour.frameName === FrameName.WATER_SAW_MILL,
			) as Array<TileNeighbour>;

		return [...lumberMills, ...industrialLumberMills, ...waterSawMills].map(
			({ tileIndex }: TileNeighbour): TileIndex => tileIndex,
		);
	}

	public get canUpgradeTo(): Set<FrameName> {
		if (this.cachedCanUpgradeTo) return this.cachedCanUpgradeTo;

		const upgrades = new Set<FrameName>(super.canUpgradeTo);
		if (!this.upgrades.size) return (this.cachedCanUpgradeTo = upgrades);

		const ssr = useSiegeSystemRepository();
		if (!ssr.isUnlocked || !this.isBlockedByEnemy) {
			for (const frameName of tilerepoFriendlyUnitsFrameNames) {
				upgrades.delete(frameName);
			}
		}

		return (this.cachedCanUpgradeTo = upgrades);
	}
}

export default ForestTerrainTileEntity;
